import { Box, Chip, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { NumericTextField } from '../../../../components/forms/NumericTextField';
import type { RockTCSGeneral, RockTCSUltrasonicReading } from '../../../../types/rockTcs';
import { computeUltrasonicPreview } from '../computations';
import { DerivedValue, fmt, inputSx, paperSx, SectionHeader, unit } from '../sectionStyles';

interface Props {
  general: RockTCSGeneral;
  ultrasonic: RockTCSUltrasonicReading[];
  onChange: (field: keyof RockTCSGeneral, value: number | null) => void;
  onUltrasonicChange: (rows: RockTCSUltrasonicReading[]) => void;
  readOnly?: boolean;
}

/**
 * REQ-02 — Ultrasonic velocity: 4 × (velocity, time) readings, reported
 * averages, and the advisory consistency checker (|v·t − 150 mm| > tolerance →
 * "Check"). The tolerance is editable per test (default 0.35 mm, D3).
 */
export const UltrasonicSection = ({ general, ultrasonic, onChange, onUltrasonicChange, readOnly }: Props) => {
  const preview = computeUltrasonicPreview(
    ultrasonic.map((r) => ({ velocityMS: r.velocityMS, timeUS: r.timeUS })),
    general.ultrasonicCheckTolerance,
  );

  const setReading = (readingNumber: number, field: 'velocityMS' | 'timeUS', value: number | null) => {
    onUltrasonicChange(ultrasonic.map((r) => (r.readingNumber === readingNumber ? { ...r, [field]: value } : r)));
  };

  return (
    <Paper variant="outlined" sx={paperSx}>
      <SectionHeader label="3. Ultrasonic Velocity (ACS — UK1401 Surfer)" />
      <Box sx={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 1.5 }}>
        <Table size="small" sx={{ '& td, & th': { p: 0.4, border: 0 } }}>
          <TableHead>
            <TableRow>
              {['#', 'Velocity (m/s)', 'Time (µs)', 'Distance (mm)', 'Consistency'].map((h) => (
                <TableCell key={h} align={h === '#' ? 'left' : 'center'}>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.65rem' }}>{h}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {ultrasonic.map((r, i) => (
              <TableRow key={r.readingNumber}>
                <TableCell>
                  <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>{r.readingNumber}</Typography>
                </TableCell>
                <TableCell align="center">
                  <NumericTextField
                    size="small" value={r.velocityMS}
                    onChange={(v) => setReading(r.readingNumber, 'velocityMS', v)}
                    InputProps={{ readOnly }} sx={{ ...inputSx, maxWidth: 120 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <NumericTextField
                    size="small" value={r.timeUS}
                    onChange={(v) => setReading(r.readingNumber, 'timeUS', v)}
                    InputProps={{ readOnly }} sx={{ ...inputSx, maxWidth: 120 }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>
                    {fmt(preview.rows[i]?.distanceMm ?? null, 2)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {preview.rows[i]?.status ? (
                    <Chip
                      size="small"
                      label={preview.rows[i].status}
                      color={preview.rows[i].status === 'Good' ? 'success' : 'warning'}
                      variant={preview.rows[i].status === 'Good' ? 'outlined' : 'filled'}
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">—</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ display: 'grid', rowGap: 1, alignContent: 'start' }}>
          <NumericTextField
            size="small" label="Checker tolerance"
            value={general.ultrasonicCheckTolerance}
            onChange={(v) => onChange('ultrasonicCheckTolerance', v)}
            InputProps={{ endAdornment: unit('mm'), readOnly }}
            sx={inputSx}
            helperText="Advisory only — does not block submission"
          />
          <DerivedValue label="Average Velocity (reported)" value={fmt(preview.avgVelocityMS, 0)} suffix="m/s" />
          <DerivedValue label="Average Time (reported)" value={fmt(preview.avgTimeUS, 1)} suffix="µs" />
          <DerivedValue
            label="Average Distance"
            value={fmt(
              (() => {
                const ds = preview.rows.map((r) => r.distanceMm).filter((d): d is number => d !== null);
                return ds.length ? ds.reduce((s, d) => s + d, 0) / ds.length : null;
              })(),
              4,
            )}
            suffix="mm"
          />
        </Box>
      </Box>
    </Paper>
  );
};
